import clsx from 'clsx'
import { useState } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'
import { useBeaconLogClick } from 'libs/athos/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { ResultImage } from 'libs/athos/components/ResultImage'
import { Price } from 'libs/athos/components/Price'
import { formatMoney } from 'helpers/utils'

const UPSELL_IMAGE_WIDTHS = [160, 240, 320, 480]

const getOptionValue = (variant, matcher) => {
  const opts = variant?.options
  if (opts && typeof opts === 'object' && !Array.isArray(opts)) {
    for (const [key, val] of Object.entries(opts)) {
      if (matcher(key)) {
        return val && typeof val === 'object' ? val.value ?? null : val
      }
    }
  }
  return null
}

const getDisplaySize = (variant) => {
  const optionValue =
    getOptionValue(variant, (key) => /size|value/i.test(key)) ??
    variant?.size ??
    variant?.Size ??
    getOptionValue(variant, (key) => !/colou?r/i.test(key))

  const label =
    optionValue ??
    variant?.option2 ??
    variant?.option1 ??
    variant?.title ??
    variant?.name

  return label != null && String(label).trim() !== '' ? String(label).trim() : 'N/A'
}

const isAvailable = (variant) => variant?.available !== false && variant?.available !== 'false'

/**
 * Price display used when a caller supplies `options.priceOverride` (e.g. GWP
 * discounted / free gifts). Values are in cents. Mirrors the sale styling used
 * elsewhere: "Free" for a zero price, brand-sale colour otherwise, with the
 * original price struck through when a discount is applied.
 */
function OverridePrice({ price, compareAtPrice, className }) {
  const isFree = !price || price <= 0
  const showCompare = compareAtPrice != null && price < compareAtPrice
  return (
    <p className={clsx(className)}>
      <span className="paragraph-xs lg:paragraph-s font-medium text-type-black-primary uppercase">
        {isFree ? 'Free' : formatMoney(price)}
      </span>
      {showCompare && (
        <span className="line-through paragraph-xs lg:paragraph-s text-type-black-tertiary">
          {formatMoney(compareAtPrice)}
        </span>
      )}
    </p>
  )
}

function SizeSelect({ variants, selectedVariant, onSelect, optionName }) {
  return (
    <div className="grow-0 min-w-0 flex relative h-[36px] w-[120px] basis-[content] bg-button-white-default rounded-full">
      <select
        className="appearance-none w-[120px] h-full pl-0.625 pr-1.5 py-0.25 text-primary paragraph-xs rounded-full bg-none bg-transparent !border-none !outline-none !ring-0 !ring-offset-0 transition-all cursor-pointer"
        value={selectedVariant ? String(selectedVariant.id) : ''}
        onChange={(event) => {
          const { value } = event.currentTarget
          onSelect(variants.find((variant) => String(variant.id) === value) || null)
        }}
        aria-label={`Select ${optionName}`}
      >
        <option value="" disabled>
          Select  {optionName}
        </option>
        {variants.map((variant) => {
          const available = isAvailable(variant)
          return (
            <option key={variant.id} value={variant.id} disabled={!available}>
              {getDisplaySize(variant)}
              {available ? '' : ' — Sold Out'}
            </option>
          )
        })}
      </select>
      <span className="pointer-events-none absolute top-1/2 right-0.5 -translate-y-1/2 flex items-center w-[22px] h-[12px] children:size-full">
        <Icon id="chevron-down" />
      </span>
    </div>
  )
}

export function ResultUpsellCard({ result, className, dataIndex = '0', options = {} }) {
  const atcText = options.atcText || 'Add To Bag'
  const soldOutText = options.soldOutText || 'Sold Out'

  const variants = Array.isArray(result.variants) ? result.variants : []

  const isGiftCard = result.is_giftcard === 'true' || result.template_suffix === 'gift-card'
  const optionName = isGiftCard ? 'Value' : 'Size'

  const productName = result.name

  const url = `/products/${result.handle}`
  const productId = result.parentId ?? result.uid

  const hasSingleVariant = variants.length <= 1
  const singleVariant = variants[0]
  const anyAvailable = variants.some(isAvailable)

  const [selectedVariant, setSelectedVariant] = useState(null)
  const [loading, setLoading] = useState(false)

  const clickLogger = useBeaconLogClick()

  const handleLogClick = () => {
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(result)
    clickLogger(result)
  }

  const addToCart = async (variant) => {
    if (!variant) return

    setLoading(true)
    try {
      if (options.onAddToCart) {
        await options.onAddToCart(variant)
      } else if (window.Cart) {
        const variantId = typeof variant.id === 'string' ? parseInt(variant.id, 10) : variant.id
        await window.Cart.addLineItems({ id: variantId, quantity: 1 })
      }
    } catch (error) {
      console.error('Error adding upsell item to cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const buttonClass = 'shrink-0 btn btn-s'

  const renderAtc = () => {
    if (!anyAvailable) {
      return (
        <div className="flex w-full">
          <button className={clsx(buttonClass, 'btn-primary btn-fill w-full')} aria-label={soldOutText} disabled>
            <span>{soldOutText}</span>
          </button>
        </div>
      )
    }

    if (hasSingleVariant) {
      return (
        <div className="flex w-full">
          <button
            type="button"
            className={clsx(buttonClass, 'btn-white btn-fill w-full', loading && 'disabled')}
            onClick={() => addToCart(singleVariant)}
            disabled={loading}
            aria-label={atcText}
          >
            <span>Add to bag</span>
          </button>
        </div>
      )
    }

    return (
      <div className="flex items-center gap-0.5">
        <SizeSelect
          variants={variants}
          selectedVariant={selectedVariant}
          onSelect={setSelectedVariant}
          optionName={optionName}
        />

          {selectedVariant ? (
            <button
              type="button"
              className={clsx(buttonClass, 'btn-white btn-fill', loading && 'disabled')}
              onClick={() => addToCart(selectedVariant)}
              disabled={loading}
              aria-label={atcText}
            >
              <span>Add</span>
            </button>
          ) : (
            <button className={clsx(buttonClass, 'btn-white btn-fill pointer-events-none')} aria-label="Select">
              <span>Add</span>
            </button>
          )}

      </div>
    )
  }

  return (
    <div
      className={clsx('group/card relative', className)}
      data-dl-track="select_item"
      data-dl-product-id={productId}
      data-dl-index={dataIndex}
    >
      <div className="flex w-full overflow-hidden rounded-lg">
        {/* Image half */}
        <a
          href={url}
          onClick={handleLogClick}
          aria-label={result.name}
          className="block min-w-[95px] shrink-0 aspect-[95/128] basis-1/5"
        >
          <ResultImage result={result} sizes="(min-width: 768px) 220px, 40vw" widths={UPSELL_IMAGE_WIDTHS} />
        </a>

        {/* Details half */}
        <div className="flex basis-4/5 shrink-0 flex-col gap-y-0.375 min-w-0 bg-page-secondary p-1">
          <a
            href={url}
            onClick={handleLogClick}
            className="paragraph-xs font-medium line-clamp-2 link-fade"
            aria-label={result.name}
            dangerouslySetInnerHTML={{ __html: productName }}
          />

          {options.priceOverride ? (
            <OverridePrice
              price={options.priceOverride.price}
              compareAtPrice={options.priceOverride.compareAtPrice}
              className="flex gap-x-0.25"
            />
          ) : (
            <Price
              price={String(result.price ?? '')}
              compareAtPrice={result.compare_at_price != null ? String(result.compare_at_price) : undefined}
              className="flex gap-x-0.25"
            />
          )}

          <div className="mt-auto">{renderAtc()}</div>
        </div>
      </div>
    </div>
  )
}
